import React, { createContext, forwardRef, useContext, useState } from "react"
import { cn } from "@/lib/utils"



/* -------------------------------- Context -------------------------------- */
interface AvatarContextType {
  isLoaded: boolean
  setLoaded: (loaded: boolean) => void
}
const AvatarContext = createContext<AvatarContextType | undefined>(undefined)

function useAvatarCtx(component: string) {
  const ctx = useContext(AvatarContext)
  if (!ctx) {
    throw new Error(`[${component}] must be used within <Avatar> root`)
  }
  return ctx
}

/* --------------------------------- Root ---------------------------------- */
interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {}

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, children, ...props }, ref) => {
    const [isLoaded, setLoaded] = useState(false)

    return (
      <AvatarContext.Provider value={{ isLoaded, setLoaded }}>
        <div
          ref={ref}
          className={cn(
            "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
            className
          )}
          {...props}
        >
          {children}
        </div>
      </AvatarContext.Provider>
    )
  }
)
Avatar.displayName = "Avatar"

/* --------------------------------- Image --------------------------------- */
interface AvatarImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {}

const AvatarImage = forwardRef<HTMLImageElement, AvatarImageProps>(
  ({ className, onLoad, onError, ...props }, ref) => {
    const { setLoaded } = useAvatarCtx("AvatarImage")

    const handleLoad: React.ReactEventHandler<HTMLImageElement> = (e) => {
      setLoaded(true)
      onLoad?.(e)
    }

    return (
      <img
        ref={ref}
        onLoad={handleLoad}
        onError={onError}
        className={cn("aspect-square h-full w-full", className)}
        {...props}
      />
    )
  }
)
AvatarImage.displayName = "AvatarImage"

/* ------------------------------- Fallback --------------------------------- */
interface AvatarFallbackProps extends React.HTMLAttributes<HTMLDivElement> {}

const AvatarFallback = forwardRef<HTMLDivElement, AvatarFallbackProps>(
  ({ className, children, ...props }, ref) => {
    const { isLoaded } = useAvatarCtx("AvatarFallback")

    if (isLoaded) return null

    return (
      <div
        ref={ref}
        className={cn(
          "flex h-full w-full items-center justify-center rounded-full bg-muted",
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)
AvatarFallback.displayName = "AvatarFallback"

/* -------------------------------- Exports -------------------------------- */
export { Avatar, AvatarImage, AvatarFallback }
